import { v3, v2, quat } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import { ConnectorHelper } from "./ConnectorHelper";
import { ConnectorType } from "./ConnectorType";
import { ListenerMixin } from "oc-commons/ListenerMixin";
import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import * as Scenes from "oc-threejs/Scenes";

import * as _ from "lodash";
import { Box3, Object3D, Plane, Quaternion, Vector3 } from "three";

/**
 * Lauffähiges Beispiel unter:
 * http://preview.objectcode.de/oc-base Examples/#oc-interaction.Connectors
 */
export class Connector extends ListenerMixin(Object3D) {
  static EPSILON = 0.000001;

  // @ts-ignore
  type: ConnectorType;
  connected: Connector | null = null;
  disabled = false;
  isRemoveOnDrop = false;
  isUnbreakable = false;
  isConnectedWithOwner = false;
  strengthFactor = 1;
  anchorPlane: Plane | null = null;
  helper: ConnectorHelper;
  pulseStrength: number = 0; // Added property used in Helper

  // Posbox properties assumed to be added by extensions or mixins not visible here.
  // JS code uses this.posbox.center.
  // Maybe Object3DExtensions adds posbox?
  // I will declare posbox as any for now or check extensions.
  posbox: any;

  constructor(type: ConnectorType | string | string[]) {
    super();
    this.type = ConnectorType.ensureType(type as any); // cast for safety
    this.connected = null;
    this.helper = new ConnectorHelper(this);
    this.add(this.helper);
    // STAGE.addAsPseudoChild(this, this.helper);
    // if (type.figure == 'Anchor') {
    //     // Anchors sollen die Bounding Box erweitern
    //     this.add(Scenes.createPointObject());
    // }
  }

  getAnchorPlane(): Plane {
    if (this.anchorPlane != null) {
      return this.anchorPlane;
    }
    return STAGE.settings.connectors.anchorPlane;
  }

  matches(other: Connector): boolean {
    const pair = (figure1: string, figure2: string) =>
      (this.type.figure == figure1 && other.type.figure == figure2) ||
      (this.type.figure == figure2 && other.type.figure == figure1);
    return (
      this.type.name == other.type.name &&
      (pair("Hook", "Eyelet") ||
        pair("Magnet", "Magnet") ||
        pair("Magnet", "Hook") ||
        pair("Magnet", "Eyelet"))
    );
  }

  isCompetitor(other: Connector): boolean {
    const pair = (figure1: string, figure2: string) =>
      (this.type.figure == figure1 && other.type.figure == figure2) ||
      (this.type.figure == figure2 && other.type.figure == figure1);
    return (
      this.type.name == other.type.name &&
      (pair("Hook", "Hook") ||
        pair("Eyelet", "Eyelet") ||
        pair("Magnet", "Magnet") ||
        pair("Magnet", "Hook") ||
        pair("Magnet", "Eyelet"))
    );
  }

  isAnchor(): boolean {
    return this.type.figure == "Anchor";
  }

  samePositionAndMatches(other: Connector): boolean {
    if (!this.matches(other)) {
      return false;
    }
    return this.samePosition(other);
  }

  samePosition(other: Connector): boolean {
    const a = this.getWorldPosition(v3());
    const b = other.getWorldPosition(v3());
    return a.distanceToSquared(b) < Connector.EPSILON;
  }

  disconnect(): void {
    const otherConnector = this.connected;
    if (!otherConnector) {
      // allready disconnected
      return;
    }
    this.trigger("disconnect", this, otherConnector);
    otherConnector.trigger("disconnect", otherConnector, this);
    otherConnector.connected = null;
    this.connected = null;
  }

  connect(otherConnector: Connector): void {
    if (
      otherConnector === this.connected &&
      otherConnector != null &&
      otherConnector.connected === this
    ) {
      // allready connected
      return;
    }
    if (this.connected) {
      this.disconnect();
    }
    if (otherConnector.connected) {
      otherConnector.disconnect();
    }
    this.connected = otherConnector;
    otherConnector.connected = this;
    this.trigger("connect", this, otherConnector);
    otherConnector.trigger("connect", otherConnector, this);
  }

  moveTo(otherConnector: Connector, slerpFactor = 1): void {
    const owner = Scenes.getLastUncommenAnchestor(this.parent as any, otherConnector as any);
    if (owner == null) {
      throw new Error("dragged object is not in scene");
    }

    const parents = Array.from(Scenes.getSelfAndAncestorSet(this as any)); // Cast this to any
    const ownerIndex = parents.indexOf(owner);
    parents.splice(ownerIndex + 1, parents.length);
    Scenes.bring({
      what: parents.reverse(),
      to: [otherConnector as any, Maths3.quaternion.y180],
      rotate: true,
      translate: true,
      slerpFactor,
    });
  }

  isFreeForDrop(): boolean {
    return (
      !this.disabled &&
      !this.isConnectedWithOwner &&
      (this.isRemoveOnDrop ||
        Connector.isParentDropExchange(this) ||
        this.connected == null ||
        (this.connected && Connector.isParentDropExchange(this.connected)))
    );
  }

  getDropPrio(): number {
    return this.isFreeForDrop() ? 0 : 1;
  }

  connectWithOwner(connectorOfOwner: Connector): any {
    if (!this.matches(connectorOfOwner)) {
      this.matches(connectorOfOwner);
      throw new Error("connectWithOwner doesn't match: " + this + " and " + connectorOfOwner);
    }
    this.isConnectedWithOwner = true;
    this.isUnbreakable = true;
    this.moveTo(connectorOfOwner);
    (this.parent as any).owner = connectorOfOwner.parent;
    this.connect(connectorOfOwner);
    this.on("disconnect", () => {
      Scenes.remove(this.parent);
      this.isConnectedWithOwner = false;
    });
    return {
      andRemoveOnDrop: () => {
        (this.parent as any).isRemoveOnDrop = true;
      },
    };
  }

  toString(): string {
    return (
      this.parent +
      "/[" +
      this.type.name +
      "," +
      this.type.figure +
      "](" +
      this.id +
      ")" +
      (this.connected ? "+" : "-")
    );
  }

  static allConnectors(): Connector[] {
    const l: Connector[] = [];
    STAGE.scene.traverse((o: any) => {
      if (Connector.isConnectorNotAnchor(o)) {
        l.push(o);
      }
    });
    return l;
  }

  static updateConnectedConnectors(): void {
    Commons.iterateWhileDirty(() => {
      // @ts-ignore
      const innerDirty = Commons.iterateWhileDirty(() => {
        let dirty = false;
        const connectors = Connector.allConnectors();
        connectors.forEach((c) => {
          if (
            c.connected &&
            (!c.samePositionAndMatches(c.connected) || Scenes.isOrphan(c.connected as any))
          ) {
            c.disconnect();
            dirty = true;
          }
        });

        reconnect: for (let i = 0; i < connectors.length; i++) {
          const a = connectors[i];
          for (let j = i + 1; j < connectors.length; j++) {
            const b = connectors[j];
            if (
              a.connected != b &&
              a.samePositionAndMatches(b) &&
              a.getDropPrio() <= b.getDropPrio() &&
              !(
                (a.connected && a.connected.parent === b.parent) ||
                (b.connected && b.connected.parent === a.parent)
              )
            ) {
              if (a.connected) {
                const old = a.connected;
                a.disconnect();
                Scenes.remove(old.parent as any);
                dirty = true;
                break reconnect;
              }
              a.connect(b);
              dirty = true;
            }
          }
        }

        return dirty;
      });

      const onUpdateConnectedConnectorsList = Scenes.filter(
        STAGE.scene,
        (o: any) => o.onUpdateConnectedConnectors,
      );
      const connector = onUpdateConnectedConnectorsList.find((o: any) =>
        o.onUpdateConnectedConnectors(),
      );
      return !!(innerDirty as any) || !!connector;
    });

    STAGE.trigger("updatedConnectedConnectors", this);
    (APP as any).trigger("updatedConnectedConnectors", this);
  }

  static applyForces(object: any, dropped: boolean, applyForces = true): void {
    const connectorSettings = STAGE.settings.connectors;

    const forces: any[] = object.getConnectorForces(); // extension method?

    if (applyForces && !Commons.isEmpty(forces)) {
      const dominating = forces[0];

      if (dropped) {
        if (
          dominating.strength > 1 - Connector.EPSILON ||
          dominating.distance < connectorSettings.snapDistance
        ) {
          dominating.own.moveTo(dominating.other);
        }
      } else {
        // Anziehung / Ausrichtung
        if (object.rotateIfDragged) {
          const originPlane = new Plane().setFromNormalAndCoplanarPoint(
            connectorSettings.anchorPlane.normal,
            v3(0, 0, 0),
          );
          const steps = connectorSettings.forceShuffleSteps;
          const qf = quat();
          for (let i = 0; i < steps; i++) {
            (steps > 1 ? _.shuffle(_.clone(forces)) : forces).forEach((force) => {
              const xo = object.getWorldPosition(v3());
              const xa = force.own.getWorldPosition(v3());
              const xb = force.other.getWorldPosition(v3());
              const inverseObjectQuaternion = object.quaternion.clone().invert();
              const planeVector = (v: Vector3) => originPlane.projectPoint(v, v3());
              const dxo = (v: Vector3) => v.clone().sub(xo);
              const unitVector = (v: Vector3) =>
                planeVector(dxo(v)).normalize().applyQuaternion(inverseObjectQuaternion);
              const q = new Quaternion().setFromUnitVectors(unitVector(xa), unitVector(xb));
              qf.slerp(q, force.strength);
            });
          }
          object.quaternion.multiply(qf);
          object.updateMatrixWorld();
        }

        // snap während Drag
        if (dominating.distance < connectorSettings.snapDistance) {
          const av = dominating.own.getWorldPosition(v3());
          const bv = dominating.other.getWorldPosition(v3());
          const dv = bv.clone().sub(av);
          object.position.add(dv);
          object.updateMatrixWorld();
        }
      }
    }

    const connectors = Connector.allConnectors();
    connectors.forEach((x) => (x.pulseStrength = 0));
    forces.forEach((force) => {
      force.own.pulseStrength = Math.max(force.pulseStrength, force.own.pulseStrength);
      force.other.pulseStrength = Math.max(force.pulseStrength, force.other.pulseStrength);
    });
  }

  static isParentDropExchange(c: Connector): boolean {
    return (c.parent as any).isRemoveOnDrop || (c.parent as any).isDropExchange;
  }

  static isConnectorNotAnchor(x: any): boolean {
    return x instanceof Connector && x.type.figure != "Anchor";
  }

  static getConnectorForce(
    distance: number,
    distanceWhereForceIs0: number,
    distanceWhereForceIs1: number,
  ): number {
    const strength = Connector.getConnectorForceUnbounded(
      distance,
      distanceWhereForceIs0,
      distanceWhereForceIs1,
    );
    return Math.min(strength, 1);
  }

  static getConnectorForceUnbounded(
    distance: number,
    distanceWhereForceIs0: number,
    distanceWhereForceIs1: number,
    forceFactor = 1,
    connectorAngle = 0,
    angle0 = -0.0001,
    openingAngleForce = 1,
    angle1 = Math.PI + 0.0001,
  ): number {
    if (distance < Connector.EPSILON) return Number.MAX_VALUE;
    if (distance > distanceWhereForceIs0) return 0;

    const angleForce =
      Maths.mapToUnitInterval(angle0, connectorAngle, angle1) * (1 - openingAngleForce) +
      openingAngleForce;

    let normalizedDistance =
      (distanceWhereForceIs0 - distance) / (distanceWhereForceIs0 - distanceWhereForceIs1);

    let force = Math.pow(normalizedDistance, 2) * forceFactor * angleForce;

    return force;
  }

  setConnectorOptions(options: any): void {
    if (options.isRemoveOnDrop) {
      this.isRemoveOnDrop = true;
    }
    if (options.isUnbreakable) {
      this.isUnbreakable = true;
    }
    if (options.position) {
      this.position.copy(options.position);
    }
    if (options.quaternion) {
      this.quaternion.copy(options.quaternion);
    }
    if (options.userData) {
      this.userData = Commons.cloneDeep(options.userData);
    }
    if (options.anchorPlane) {
      const plane = {
        normal: options.anchorPlane.normal ? options.anchorPlane.normal : v3(0, 1, 0),
        constant: options.anchorPlane.constant ? options.anchorPlane.constant : 0,
      };
      this.anchorPlane = new Plane(plane.normal.clone(), plane.constant);
    }
  }

  static connectorOptions = [
    "isRemoveOnDrop",
    "isUnbreakable",
    "position",
    "quaternion",
    "userData",
    "anchorPlane",
  ];

  getBoundingBox(): Box3 {
    return new Box3(v3(0, 0, 0), v3(0, 0, 0));
  }

  aroundParent(gridPosition: Vector3, orientationOrthogonalToFace = false): Connector {
    this.posbox.center = (this.parent as any).posbox.grid(
      gridPosition.x,
      gridPosition.y,
      gridPosition.z,
    );
    let centerOfParentDirection = (this.parent as any).posbox.center
      .clone()
      .sub(this.posbox.center)
      .normalize();
    if (orientationOrthogonalToFace) {
      centerOfParentDirection = Maths3.nearestBoxFaceNormal(centerOfParentDirection);
    }
    this.quaternion.setFromUnitVectors(Maths3.vector.y, centerOfParentDirection);
    return this;
  }
}
